import { useState } from 'react';
import {
  dayKey,
  endOfMonth,
  monthLabel,
  parseDay,
  startOfMonth,
} from '../lib/date';
import { PixelButton } from './PixelButton';
import { Triangle } from './Triangle';

/**
 * Inline pixel calendar for picking a single day (YYYY-MM-DD). Mirrors the
 * chrome used by CalendarScreen so the whole app speaks one calendar dialect.
 */
export function DatePicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const initial = value ? parseDay(value) : new Date();
  const [cursor, setCursor] = useState(() => startOfMonth(initial));

  const today = dayKey(new Date());
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const startDow = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const cells: ({ day: number; key: string } | null)[] = [];
  const totalCells = Math.ceil((startDow + daysInMonth) / 7) * 7;
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startDow + 1;
    if (dayNum < 1 || dayNum > daysInMonth) {
      cells.push(null);
      continue;
    }
    const d = new Date(cursor.getFullYear(), cursor.getMonth(), dayNum);
    cells.push({ day: dayNum, key: dayKey(d) });
  }

  const shift = (delta: number) =>
    setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1));

  return (
    <div className="date-picker">
      <div className="cal-header">
        <PixelButton onClick={() => shift(-1)} aria-label="previous month">
          <Triangle direction="left" />
        </PixelButton>
        <div className="cal-header__title">{monthLabel(cursor)}</div>
        <PixelButton onClick={() => shift(1)} aria-label="next month">
          <Triangle direction="right" />
        </PixelButton>
      </div>
      <div className="calendar-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="cal-dow">{d}</div>
        ))}
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="cal-cell cal-cell--empty" />;
          const isSelected = c.key === value;
          const isToday = c.key === today;
          const cls = [
            'cal-cell',
            'cal-cell--pickable',
            isSelected && 'cal-cell--selected',
            !isSelected && isToday && 'cal-cell--today',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={i}
              type="button"
              className={cls}
              onClick={() => onChange(c.key)}
              aria-pressed={isSelected}
              aria-label={c.key}
            >
              {c.day}
            </button>
          );
        })}
      </div>
      {value && (
        <div className="date-picker__footer">
          <PixelButton onClick={() => onChange(null)}>CLEAR</PixelButton>
        </div>
      )}
    </div>
  );
}
